import { useEffect, useState, type ReactNode } from 'react';
import { api } from '../api.js';
import { storageEstimate } from '../db/db.js';
import { syncer } from '../sync/sync.js';
import { useNavigate } from 'react-router-dom';
import { useApp, type Settings } from '../state.js';
import TopBar from '../ui/TopBar.js';

interface Usage {
  budgetUsd: number; model: string; promptVersion: string;
  months: { month: string; runs: number; inputTokens: number; outputTokens: number; costUsd: number }[];
}

interface TodoistStatus { connected: boolean; projects: unknown[]; labels: unknown[] }

function Section({ title, desc, children, aside }: {
  title: string; desc?: string; children: ReactNode; aside?: ReactNode;
}): JSX.Element {
  return (
    <section>
      <header>
        <h2>{title}</h2>
        {aside}
      </header>
      {desc ? <p className="desc">{desc}</p> : null}
      <div className="body">{children}</div>
    </section>
  );
}

function Switch({ checked, onChange, label, hint }: {
  checked: boolean; onChange(v: boolean): void; label: string; hint?: string;
}): JSX.Element {
  return (
    <div className="field">
      <label className="switch">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span className="label">{label}</span>
      </label>
      {hint ? <span className="hint" style={{ paddingLeft: 30 }}>{hint}</span> : null}
    </div>
  );
}

export default function SettingsView(): JSX.Element {
  const navigate = useNavigate();
  const { settings, setSettings, showToast, debug, toggleDebug } = useApp();
  const [draft, setDraft] = useState<Settings | null>(settings);
  const [token, setToken] = useState('');
  const [todoist, setTodoist] = useState<TodoistStatus | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [storage, setStorage] = useState<{ usedMb: number; quotaMb: number } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(settings), [settings]);
  useEffect(() => {
    void api.get<TodoistStatus>('/api/todoist/status').then(setTodoist).catch(() => null);
    void api.get<Usage>('/api/ai/usage').then(setUsage).catch(() => null);
    void storageEstimate().then(setStorage);
  }, []);

  if (!draft) return <div className="center"><p className="muted">Carregando…</p></div>;

  const set = (patch: Partial<Settings>): void => setDraft({ ...draft, ...patch });
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);

  async function save(): Promise<void> {
    if (!draft) return;
    setSaving(true);
    try {
      await api.put('/api/settings', {
        legend: draft.legend, rules: draft.rules, vocabulary: draft.vocabulary,
        defaultPaper: draft.defaultPaper, fingerDraw: draft.fingerDraw,
        palmRejectPx: draft.palmRejectPx, lowLatencyInk: draft.lowLatencyInk,
        autoSendHighConfidence: draft.autoSendHighConfidence,
      });
      setSettings(draft);
      showToast('Ajustes salvos.');
    } catch {
      showToast('Não consegui salvar. Sem internet?');
    } finally {
      setSaving(false);
    }
  }

  async function connect(): Promise<void> {
    try {
      await api.post('/api/todoist/connect', { token });
      setToken('');
      setTodoist(await api.get<TodoistStatus>('/api/todoist/status'));
      showToast('Todoist conectado.');
    } catch {
      showToast('O Todoist recusou este token.');
    }
  }

  const month = usage?.months[0];

  return (
    <>
      <TopBar title="Ajustes" back="/">
        {dirty ? <span className="badge warn">alterações não salvas</span> : null}
        <button className="primary" onClick={() => void save()} disabled={saving || !dirty}>
          {saving ? 'Salvando…' : 'Salvar'}
        </button>
      </TopBar>

      <div className="content">
        <div className="settings">
          <Section
            title="Todoist"
            desc="Onde as tarefas confirmadas aparecem no celular. O token fica criptografado no servidor e nunca sai daqui."
            aside={todoist ? (
              <span className={`badge ${todoist.connected ? 'ok' : ''}`}>
                {todoist.connected ? 'conectado' : 'não conectado'}
              </span>
            ) : null}
          >
            {todoist?.connected ? (
              <>
                <div className="kv"><span>Projetos em cache</span><span>{todoist.projects.length}</span></div>
                <div className="kv"><span>Etiquetas em cache</span><span>{todoist.labels.length}</span></div>
                <hr className="divider" />
                <div className="row wrap">
                  <button onClick={() => void api.post('/api/todoist/refresh').then(() => showToast('Cache atualizado.'))}>
                    Atualizar projetos e etiquetas
                  </button>
                  <button
                    className="danger"
                    onClick={() => void api.post('/api/todoist/disconnect')
                      .then(() => setTodoist({ connected: false, projects: [], labels: [] }))}
                  >
                    Desconectar
                  </button>
                </div>
              </>
            ) : (
              <div className="field">
                <span className="label">Token pessoal</span>
                <span className="hint">Todoist → Configurações → Integrações → Desenvolvedor.</span>
                <input value={token} onChange={(e) => setToken(e.target.value)} placeholder="Cole o token aqui" />
                <div>
                  <button className="primary" onClick={() => void connect()} disabled={token.trim().length < 10}>
                    Conectar
                  </button>
                </div>
              </div>
            )}
          </Section>

          <Section
            title="Escrita"
            desc="Como a caneta se comporta. Mexa aqui se a mão riscar a página ou se a tela se comportar mal ao escrever."
          >
            <Switch
              checked={draft.lowLatencyInk !== false}
              onChange={(v) => set({ lowLatencyInk: v })}
              label="Modo de baixa latência"
              hint="Faz o traço acompanhar a caneta de perto. Em alguns tablets Android o driver devolve essa camada opaca e a tela fica toda preta ao escrever — se isso acontecer, desligue aqui e volte à página."
            />
            <hr className="divider" />
            <div className="field">
              <span className="label">Rejeição de palma</span>
              <span className="hint">
                Ignorar toques maiores que <strong>{draft.palmRejectPx} px</strong>.
                Suba se a mão continuar riscando; desça se a caneta falhar em algum ângulo.
              </span>
              <input
                type="range" min={20} max={80} value={draft.palmRejectPx}
                onChange={(e) => set({ palmRejectPx: Number(e.target.value) })}
              />
            </div>
            <hr className="divider" />
            <div className="field">
              <span className="label">Papel padrão</span>
              <select value={draft.defaultPaper} onChange={(e) => set({ defaultPaper: e.target.value as 'ruled' })}>
                <option value="ruled">Pautado</option>
                <option value="grid">Quadriculado</option>
                <option value="dotted">Pontilhado</option>
                <option value="blank">Liso</option>
              </select>
            </div>
            <hr className="divider" />
            <Switch
              checked={draft.fingerDraw}
              onChange={(v) => set({ fingerDraw: v })}
              label="Escrever com o dedo"
              hint="Só para testar sem a caneta por perto. Com isto ligado, um dedo não rola mais a página."
            />
            <Switch
              checked={debug}
              onChange={toggleDebug}
              label="Mostrar medições na página"
              hint="Exibe fps, latência e tempo de quadro no canto. É o número que decide se o motor de escrita fica."
            />
          </Section>

          <Section
            title="Legenda de símbolos"
            desc="O que cada símbolo significa. Entra no prompt da leitura — mudar aqui muda como a IA interpreta a linha."
          >
            {/* A legenda tem 13 linhas: cortar o fim esconde justamente os
                símbolos novos (X, > e @@). */}
            <textarea
              style={{ minHeight: 280 }} value={draft.legend} spellCheck={false}
              onChange={(e) => set({ legend: e.target.value })}
            />
          </Section>

          <Section
            title="Regras pessoais"
            desc="Uma regra por linha. O que estiver escrito na própria linha vence a regra."
          >
            <textarea
              style={{ minHeight: 130 }} value={draft.rules} spellCheck={false}
              onChange={(e) => set({ rules: e.target.value })}
            />
          </Section>

          <Section
            title="Vocabulário"
            desc="Termos que a IA costuma errar: nomes de norma, produto, fornecedor, pessoa."
          >
            <textarea
              style={{ minHeight: 90 }} value={draft.vocabulary} spellCheck={false}
              onChange={(e) => set({ vocabulary: e.target.value })}
            />
          </Section>

          <Section title="Revisão" desc="O que acontece depois que a IA lê a página.">
            <Switch
              checked={draft.autoSendHighConfidence}
              onChange={(v) => set({ autoSendHighConfidence: v })}
              label="Enviar direto os itens com confiança alta e sem dúvida"
              hint="Sai desligado de propósito. Ligue quando a leitura já estiver acertando sempre."
            />
          </Section>

          <Section
            title="Custo da leitura"
            aside={usage ? <span className="badge">{usage.model}</span> : null}
            desc="Cada Enviar manda uma imagem da página para a Anthropic. O teto mensal é configurado no servidor."
          >
            {month ? (
              <>
                <div className="kv"><span>{month.month}</span><span>{month.runs} leituras</span></div>
                <div className="kv">
                  <span>Gasto no mês</span>
                  <span>US$ {month.costUsd.toFixed(2)} de US$ {usage?.budgetUsd.toFixed(2)}</span>
                </div>
                <div className="kv"><span>Versão do prompt</span><span>{usage?.promptVersion}</span></div>
              </>
            ) : (
              <p className="muted small" style={{ margin: 0 }}>Nenhuma leitura ainda.</p>
            )}
          </Section>

          <Section title="Dados neste aparelho" desc="Tudo é salvo aqui primeiro e sincronizado depois.">
            {storage ? (
              <div className="kv">
                <span>Espaço usado</span>
                <span>{storage.usedMb} MB de {storage.quotaMb} MB</span>
              </div>
            ) : null}
            <hr className="divider" />
            <div className="row wrap">
              <button onClick={() => void syncer.run().then(() => showToast('Sincronizado.'))}>
                Sincronizar agora
              </button>
              <button onClick={() => void syncer.fullPull().then(() => showToast('Tudo baixado de novo.'))}>
                Baixar tudo de novo
              </button>
            </div>
          </Section>

          <Section
            title="Versão e diagnóstico"
            desc="Se a escrita se comportar mal neste aparelho, é aqui que dá para descobrir o motivo."
          >
            <div className="kv">
              <span>Build instalado</span>
              <span>{typeof __BUILD__ === 'string' ? __BUILD__ : '(desconhecido)'}</span>
            </div>
            <hr className="divider" />
            <div className="row wrap">
              <button onClick={() => navigate('/diag')}>Abrir diagnóstico</button>
              <button
                onClick={() => {
                  // Descarta o service worker e recarrega: o caminho de fuga
                  // quando o aparelho insiste numa versão antiga.
                  void (async () => {
                    for (const r of await navigator.serviceWorker.getRegistrations()) await r.unregister();
                    for (const k of await caches.keys()) await caches.delete(k);
                    location.reload();
                  })();
                }}
              >
                Forçar atualização do app
              </button>
            </div>
          </Section>

          <Section title="Sessão">
            <div>
              <button
                className="danger"
                onClick={() => void api.post('/api/auth/logout').then(() => location.reload())}
              >
                Sair deste aparelho
              </button>
            </div>
          </Section>
        </div>
      </div>
    </>
  );
}
